import {
  collection,
  doc,
  increment,
  updateDoc,
  writeBatch,
  arrayUnion,
  serverTimestamp,
  type CollectionReference,
  type FirestoreError,
} from "firebase/firestore";
import { firestore } from "@/helpers/firebase/firebase";
import { quoteDocumentConverter } from "@kekkle/shared/convertors/web/quote-document.converter";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import {
  countDocuments,
  withUpdatedAt,
  subscribeToAllDocuments,
  subscribeToDocumentChanges,
  type DeltaSyncSnapshot,
  type FullSyncSnapshot,
} from "@/helpers/firestore/DeltaSyncService";
import { generateTrigrams } from "@/helpers/utils/trigrams";

/**
 * Returns a reference to the quotes collection for a specific group with converter
 */
function quotesCollection(groupId: string): CollectionReference<QuoteDocument> {
  return collection(firestore, `groups/${groupId}/quotes`).withConverter(
    quoteDocumentConverter,
  );
}

/**
 * Listens to every quote in a group, ordered by timestamp descending.
 * Used for the first (or a recovery) full sync; see DeltaSyncService.
 * Returns an unsubscribe function.
 */
export function subscribeToAllQuotes(
  groupId: string,
  callbacks: {
    onSnapshot: (snapshot: FullSyncSnapshot<QuoteDocument>) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  return subscribeToAllDocuments(quotesCollection(groupId), callbacks);
}

/**
 * Listens to quotes whose updated_at is newer than the cursor (epoch ms).
 * Returns an unsubscribe function.
 */
export function subscribeToQuoteChanges(
  groupId: string,
  cursor: number,
  callbacks: {
    onSnapshot: (snapshot: DeltaSyncSnapshot<QuoteDocument>) => void;
    onError: (error: FirestoreError) => void;
  },
): () => void {
  return subscribeToDocumentChanges(
    quotesCollection(groupId),
    cursor,
    callbacks,
  );
}

/**
 * Exact number of quotes in a group, counted on the server.
 * Used to detect deletes that a delta listener cannot see.
 */
export async function countQuotes(groupId: string): Promise<number> {
  return countDocuments(quotesCollection(groupId));
}

/**
 * Generates the id for a new quote without writing anything, so the quote
 * can be shown locally before `addQuote` is confirmed.
 */
export function createQuoteId(groupId: string): string {
  return doc(quotesCollection(groupId)).id;
}

/**
 * Adds a new quote to the group, using the id from `createQuoteId`
 */
export async function addQuote(
  groupId: string,
  data: QuoteDocument,
): Promise<void> {
  const batch = writeBatch(firestore);
  const quoteCollection = quotesCollection(groupId);
  const newQuoteRef = doc(quoteCollection, data._id);
  batch.set(newQuoteRef, withUpdatedAt(data));
  const groupCollection = collection(firestore, "groups/");
  const groupRef = doc(groupCollection, groupId);
  batch.update(groupRef, {
    "general_data.number_of_quotes": increment(1),
  });
  return await batch.commit();
}

/**
 * Updates a quote in the group
 */
export async function updateQuote(
  groupId: string,
  quoteId: string,
  data: Partial<Omit<QuoteDocument, "_id">>,
): Promise<void> {
  try {
    const quoteCollection = quotesCollection(groupId);
    const quoteRef = doc(quoteCollection, quoteId);

    // Generate trigrams if quote text is being updated
    const updateData: Record<string, unknown> = withUpdatedAt(data);
    if (data.quote) {
      updateData.trigrams = generateTrigrams(data.quote);
    }

    await updateDoc(quoteRef, updateData);
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Deletes a quote from the group
 */
export async function deleteQuote(
  groupId: string,
  quoteId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const quoteCollection = quotesCollection(groupId);
    const quoteRef = doc(quoteCollection, quoteId);
    const groupCollection = collection(firestore, "groups/");
    const groupRef = doc(groupCollection, groupId);

    batch.delete(quoteRef);
    batch.update(groupRef, {
      "general_data.number_of_quotes": increment(-1),
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}

/**
 * Reports a quote and creates a report document
 */
export async function reportQuote(
  groupId: string,
  quoteId: string,
  reporterId: string,
): Promise<void> {
  try {
    const batch = writeBatch(firestore);
    const quoteCollection = quotesCollection(groupId);
    const quoteRef = doc(quoteCollection, quoteId);

    // Update quote with reporter UUID
    batch.update(
      quoteRef,
      withUpdatedAt({ reported_by: arrayUnion(reporterId) }),
    );

    // Create report document
    const reportsCollection = collection(firestore, "reports");
    const reportRef = doc(reportsCollection);
    batch.set(reportRef, {
      group_id: groupId,
      type: "quote",
      document_id: quoteId,
      created_at: serverTimestamp(),
      created_by: reporterId,
      solved: false,
      solved_at: null,
    });

    await batch.commit();
  } catch (e) {
    handleFirestoreException({ e: e as FirestoreError });
    throw e;
  }
}
